<template>
    <eqt-table @refresh="refresh" @delete="deleteItem" :total="total" :loading="loading" :title="$t('Pages.RecordBooks.title')" :subtitle="$t('Common.table_subtitle')" :items="items" :headers="headers">
      <template slot="filters">
        <v-flex xs12>
          <v-expansion-panel>
            <v-expansion-panel-content>
              <template slot="header">
                <div class="title font-weight-regular">{{ $t('Core.Fields.advanced_search')}}</div>
              </template>
              <v-card>
                <v-card-text>
                  <v-layout>
                    <v-flex xs12 md4>
                      <v-autocomplete
                        v-model="search.types"
                        :items="recordBookTypes"
                        name="recordBookTypes"
                        color="indigo"
                        hide-no-data
                        hide-selected
                        item-text="name"
                        :label="$t('Pages.RecordBookTypes.title_singular')"
                        :placeholder="$t('Common.type_to_search')"
                        prepend-icon="mdi-book-open"
                        return-object
                        multiple
                        clearable
                      ></v-autocomplete>
                    </v-flex>
                    <v-flex xs12 md4>
                      <v-autocomplete
                        v-model="search.documentTypes"
                        :items="documentTypes"
                        name="documentTypes"
                        color="indigo"
                        hide-no-data
                        hide-selected
                        item-text="name"
                        :label="$t('Pages.DocumentTypes.title_singular')"
                        :placeholder="$t('Common.type_to_search')"
                        prepend-icon="mdi-book-open"
                        return-object
                        multiple
                        clearable
                      ></v-autocomplete>
                    </v-flex>
                    <v-flex xs12 md4>
                      <v-autocomplete
                        v-model="search.realLocations"
                        :items="realLocations"
                        name="realLocations"
                        color="indigo"
                        hide-no-data
                        hide-selected
                        item-text="name"
                        :label="$t('Pages.RealLocations.title_singular')"
                        :placeholder="$t('Common.type_to_search')"
                        prepend-icon="mdi-map-marker"
                        return-object
                        multiple
                        clearable
                      ></v-autocomplete>
                    </v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex xs12 md4>
                      <v-autocomplete
                        v-model="search.importTransportTypes"
                        :items="transportTypes"
                        name="importTransportTypes"
                        color="indigo"
                        hide-no-data
                        hide-selected
                        item-text="name"
                        :label="$t('Pages.RecordBooks.model.import_transport_type')"
                        :placeholder="$t('Common.type_to_search')"
                        prepend-icon="mdi-truck"
                        return-object
                        multiple
                        clearable
                      ></v-autocomplete>
                    </v-flex>
                    <v-flex xs12 md4>
                      <v-autocomplete
                        v-model="search.exportTransportTypes"
                        :items="transportTypes"
                        name="exportTransportTypes"
                        color="indigo"
                        hide-no-data
                        hide-selected
                        item-text="name"
                        :label="$t('Pages.RecordBooks.model.export_transport_type')"
                        :placeholder="$t('Common.type_to_search')"
                        prepend-icon="mdi-truck"
                        return-object
                        multiple
                        clearable
                      ></v-autocomplete>
                    </v-flex>
                    <v-flex xs12 md4>
                      <v-autocomplete
                        :search-input.sync="qPartner"
                        v-model="search.partners"
                        :items="partners"
                        :loading="partnerLoading"
                        name="partners"
                        color="indigo"
                        hide-no-data
                        hide-selected
                        item-text="name"
                        :label="$t('Pages.Partners.title_singular')"
                        :placeholder="$t('Common.type_to_search')"
                        prepend-icon="mdi-account-supervisor"
                        return-object
                        multiple
                        clearable
                        :item-text="text"
                      ></v-autocomplete>
                    </v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex xs12 md6>
                      <v-dialog
                        ref="startDate"
                        v-model="startDate"
                        :return-value.sync="search.startDate"
                        persistent
                        lazy
                        full-width
                        width="290px"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="computedStartDate"
                          :label="$t('Pages.RecordBooks.model.start_date')"
                          prepend-icon="event"
                          readonly
                          color="indigo"
                          clearable
                          name="startDate"
                          required
                        ></v-text-field>
                        <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.date_start" scrollable>
                          <v-spacer></v-spacer>
                          <v-btn flat @click="startDate = false">{{$t('Core.Fields.cancel')}}</v-btn>
                          <v-btn flat color="indigo" @click="$refs.startDate.save(search.date_start)">{{$t('Core.Fields.ok')}}</v-btn>
                        </v-date-picker>
                      </v-dialog>
                    </v-flex>
                    <v-flex xs12 md6>
                      <v-dialog
                        ref="endDate"
                        v-model="endDate"
                        :return-value.sync="search.endDate"
                        persistent
                        lazy
                        full-width
                        width="290px"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="computedEndDate"
                          :label="$t('Pages.RecordBooks.model.end_date')"
                          prepend-icon="event"
                          readonly
                          color="indigo"
                          clearable
                          name="endDate"
                          required
                        ></v-text-field>
                        <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.date_end" scrollable>
                          <v-spacer></v-spacer>
                          <v-btn flat @click="endDate = false">{{$t('Core.Fields.cancel')}}</v-btn>
                          <v-btn flat color="indigo" @click="$refs.endDate.save(search.date_end)">{{$t('Core.Fields.ok')}}</v-btn>
                        </v-date-picker>
                      </v-dialog>
                    </v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex xs12 md6>
                      <v-dialog
                        ref="startArchiveDate"
                        v-model="startArchiveDate"
                        :return-value.sync="search.startArchiveDate"
                        persistent
                        lazy
                        full-width
                        width="290px"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="computedStartArchiveDate"
                          :label="$t('Pages.RecordBooks.model.start_archive_date')"
                          prepend-icon="event"
                          readonly
                          color="indigo"
                          clearable
                          name="startArchiveDate"
                          required
                        ></v-text-field>
                        <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.archive_date_start" scrollable>
                          <v-spacer></v-spacer>
                          <v-btn flat @click="startArchiveDate = false">{{$t('Core.Fields.cancel')}}</v-btn>
                          <v-btn flat color="indigo" @click="$refs.startArchiveDate.save(search.archive_date_start)">{{$t('Core.Fields.ok')}}</v-btn>
                        </v-date-picker>
                      </v-dialog>
                    </v-flex>
                    <v-flex xs12 md6>
                      <v-dialog
                        ref="endArchiveDate"
                        v-model="endArchiveDate"
                        :return-value.sync="search.endArchiveDate"
                        persistent
                        lazy
                        full-width
                        width="290px"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="computedEndArchiveDate"
                          :label="$t('Pages.RecordBooks.model.end_archive_date')"
                          prepend-icon="event"
                          readonly
                          color="indigo"
                          clearable
                          name="endArchiveDate"
                          required
                        ></v-text-field>
                        <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.archive_date_end" scrollable>
                          <v-spacer></v-spacer>
                          <v-btn flat @click="endArchiveDate = false">{{$t('Core.Fields.cancel')}}</v-btn>
                          <v-btn flat color="indigo" @click="$refs.endArchiveDate.save(search.archive_date_end)">{{$t('Core.Fields.ok')}}</v-btn>
                        </v-date-picker>
                      </v-dialog>
                    </v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex xs12 md6>
                      <v-dialog
                        ref="startArchivedDate"
                        v-model="startArchivedDate"
                        :return-value.sync="search.startArchivedDate"
                        persistent
                        lazy
                        full-width
                        width="290px"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="computedStartArchivedDate"
                          :label="$t('Pages.RecordBooks.model.start_archived_date')"
                          prepend-icon="event"
                          readonly
                          color="indigo"
                          clearable
                          name="startArchivedDate"
                          required
                        ></v-text-field>
                        <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.archived_date_start" scrollable>
                          <v-spacer></v-spacer>
                          <v-btn flat @click="startArchivedDate = false">{{$t('Core.Fields.cancel')}}</v-btn>
                          <v-btn flat color="indigo" @click="$refs.startArchivedDate.save(search.archived_date_start)">{{$t('Core.Fields.ok')}}</v-btn>
                        </v-date-picker>
                      </v-dialog>
                    </v-flex>
                    <v-flex xs12 md6>
                      <v-dialog
                        ref="endArchivedDate"
                        v-model="endArchivedDate"
                        :return-value.sync="search.endArchivedDate"
                        persistent
                        lazy
                        full-width
                        width="290px"
                      >
                        <v-text-field
                          slot="activator"
                          v-model="computedEndArchivedDate"
                          :label="$t('Pages.RecordBooks.model.end_archived_date')"
                          prepend-icon="event"
                          readonly
                          color="indigo"
                          clearable
                          name="endArchivedDate"
                          required
                        ></v-text-field>
                        <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.archived_date_end" scrollable>
                          <v-spacer></v-spacer>
                          <v-btn flat @click="endArchivedDate = false">{{$t('Core.Fields.cancel')}}</v-btn>
                          <v-btn flat color="indigo" @click="$refs.endArchivedDate.save(search.archived_date_end)">{{$t('Core.Fields.ok')}}</v-btn>
                        </v-date-picker>
                      </v-dialog>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-flex>
      </template>
      <template slot="items" slot-scope="{ item }">
          <td>{{ item.id }}</td>
          <td>
            <p>{{ item.partner.name }}</p>
            <p>{{ $t('Pages.Partners.model.pib') + ': ' + item.partner.pib }}</p>
            <p>{{ item.partner.mbr ? $t('Pages.Partners.model.mbr') + ': ' + item.partner.mbr : null }}</p>
          </td>
          <td>{{ item.documentType.name }}</td>
          <td>{{ item.number }}</td>
          <td>{{ getLocaleDateFormat(item.document_date) }}</td>
          <td>{{ item.document_number ? item.document_number : $t('Common.not_assigned') }}</td>
          <td>{{ item.importTransportType.name }}</td>
          <td>{{ item.exportTransportType.name }}</td>
          <td>{{ item.realLocation.name }}</td>
          <td>{{ getLocaleDateFormat(item.created_at.date) }}</td>
      </template>
    </eqt-table>
</template>

<script>
  import i18n from '@/i18n'
  import EqtTable from '../../components/core/Table'
  export default {
    name: "document-types-table",
    components: {EqtTable},
    props: {
      items: {
        type: Array,
        required: true
      },
      total: {
        type: Number,
        default: 0,
      },
      loading: {
        type: Boolean,
        required: true
      },
      recordBookTypes: {
        type: Array,
        default: []
      },
      documentTypes: {
        type: Array,
        default: []
      },
      realLocations: {
        type: Array,
        default: []
      },
      transportTypes: {
        type: Array,
        default: []
      }
    },
    data: () => ({
      headers: [
        {
          sortable: true,
          text: '#',
          value: 'id'
        },
        {
          sortable: true,
          text: i18n.t('Pages.RecordBooks.model.partner'),
          value: 'parner.name'
        },
        {
          sortable: false,
          text: i18n.t('Pages.RecordBooks.model.document_type'),
          value: 'documentType.name'
        },
        {
          sortable: true,
          text: i18n.t('Pages.RecordBooks.model.number'),
          value: 'number'
        },
        {
          sortable: false,
          text: i18n.t('Pages.RecordBooks.model.date'),
          value: 'document_date'
        },
        {
          sortable: true,
          text: i18n.t('Pages.RecordBooks.model.document_number'),
          value: 'document_number'
        },
        {
          sortable: false,
          text: i18n.t('Pages.RecordBooks.model.import_transport_type'),
          value: 'importTransportType.name'
        },
        {
          sortable: false,
          text: i18n.t('Pages.RecordBooks.model.export_transport_type'),
          value: 'exportTransportType.name'
        },
        {
          sortable: false,
          text: i18n.t('Pages.RecordBooks.model.real_location'),
          value: 'realLocation.name'
        },
        {
          sortable: false,
          text: i18n.t('Pages.RecordBooks.model.created_at'),
          value: 'created_at.date'
        }
      ],
      search: {},
      startDate: false,
      endDate: false,
      startArchiveDate: false,
      endArchiveDate: false,
      startArchivedDate: false,
      endArchivedDate: false,
      dataCopy: null,
      qPartner: null,
      partners: [],
      partnerLoading: false
    }),
    computed: {
      computedStartDate: {
        get() {
          return this.getLocaleDateFormat(this.search.date_start);
        },
        set(val) {
          return this.search.date_start = val
        }
      },
      computedEndDate: {
        get() {
          return this.getLocaleDateFormat(this.search.date_end);
        },
        set(val) {
          return this.search.date_end = val
        }
      },
      computedStartArchiveDate: {
        get() {
          return this.getLocaleDateFormat(this.search.archive_date_start);
        },
        set(val) {
          return this.search.archive_date_start = val
        }
      },
      computedEndArchiveDate: {
        get() {
          return this.getLocaleDateFormat(this.search.archive_date_end);
        },
        set(val) {
          return this.search.archive_date_end = val
        }
      },
      computedStartArchivedDate: {
        get() {
          return this.getLocaleDateFormat(this.search.archived_date_start);
        },
        set(val) {
          return this.search.archived_date_start = val
        }
      },
      computedEndArchivedDate: {
        get() {
          return this.getLocaleDateFormat(this.search.archived_date_end);
        },
        set(val) {
          return this.search.archived_date_end = val
        }
      },
    },
    methods: {
      refresh(data){
        this.dataCopy = data;
        data.types = [];
        data.documentTypes = [];
        data.realLocations = [];
        data.importTransportTypes = [];
        data.exportTransportTypes = [];
        data.partners = [];
        data.date_start = this.search.date_start;
        data.date_end = this.search.date_end;
        data.archive_date_start = this.search.archive_date_start;
        data.archive_date_end = this.search.archive_date_end;
        data.archived_date_start = this.search.archived_date_start;
        data.archived_date_end = this.search.archived_date_end;

        if(this.search.types) {
          this.search.types.forEach(element => {
            data.types.push(element.id)
          })
        }
        if(this.search.documentTypes) {
          this.search.documentTypes.forEach(element => {
            data.documentTypes.push(element.id)
          })
        }
        if(this.search.realLocations) {
          this.search.realLocations.forEach(element => {
            data.realLocations.push(element.id)
          })
        }
        if(this.search.importTransportTypes) {
          this.search.importTransportTypes.forEach(element => {
            data.importTransportTypes.push(element.id)
          })
        }
        if(this.search.exportTransportTypes) {
          this.search.exportTransportTypes.forEach(element => {
            data.exportTransportTypes.push(element.id)
          })
        }
        if(this.search.partners) {
          this.search.partners.forEach(element => {
            data.partners.push(element.id)
          })
        }

        this.$emit('refresh', data);
      },
      deleteItem(item){
        this.$emit('delete', item);
      },
      text: item => item.name + ' - ' + i18n.t('Pages.Partners.model.pib') + ': ' + item.pib + (item.mbr ? ' - ' + i18n.t('Pages.Partners.model.mbr') + ': ' + item.mbr : ''),
    },
    watch: {
      'search.types': function() {
        this.refresh(this.dataCopy);
      },
      'search.documentTypes': function() {
        this.refresh(this.dataCopy);
      },
      'search.realLocations': function() {
        this.refresh(this.dataCopy);
      },
      'search.importTransportTypes': function() {
        this.refresh(this.dataCopy);
      },
      'search.exportTransportTypes': function() {
        this.refresh(this.dataCopy);
      },
      'search.partners': function() {
        this.refresh(this.dataCopy);
      },
      'search.date_start': function() {
        this.refresh(this.dataCopy);
      },
      'search.date_end': function() {
        this.refresh(this.dataCopy);
      },
      'search.archive_date_start': function() {
        this.refresh(this.dataCopy);
      },
      'search.archive_date_end': function() {
        this.refresh(this.dataCopy);
      },
      'search.archived_date_start': function() {
        this.refresh(this.dataCopy);
      },
      'search.archived_date_end': function() {
        this.refresh(this.dataCopy);
      },
      qPartner: _.debounce(function(val){
        if(!val) return;
        // Items have already been loaded
        if (val.length < 3) return;

        // Items have already been requested
        if (this.partnerLoading) return;

        if (this.search) {
          this.partnerLoading = true;
          this.repositories.partner.all(val).then((response) => {
            this.partners = response.data.data;
          }).finally(() => {
            this.partnerLoading = false;
          });
        } else {
          this.search = true;
        }
      }, 500),
    }
  }
</script>

<style scoped>

</style>